import Image from "@tiptap/extension-image";

export type ImageAlign = "left" | "center" | "right";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    alignableImage: {
      setImageAlign: (align: ImageAlign | null) => ReturnType;
    };
  }
}

// Individual longhand declarations (not shorthand `margin`) so applying/clearing
// alignment never touches `width`/`height`, which resize sets directly on the
// same element's inline style.
const ALIGN_DECLARATIONS: Record<ImageAlign, [string, string][]> = {
  left: [
    ["float", "left"],
    ["margin-top", "0"],
    ["margin-right", "1rem"],
    ["margin-bottom", "0.5rem"],
    ["margin-left", "0"],
  ],
  right: [
    ["float", "right"],
    ["margin-top", "0"],
    ["margin-right", "0"],
    ["margin-bottom", "0.5rem"],
    ["margin-left", "1rem"],
  ],
  // `clear: both` forces this image below any earlier left/right-floated
  // image that's still "active" (floats stay in flow until cleared) — without
  // it, a centered image can render squeezed into the leftover space beside
  // a prior float instead of alone on its own full-width line.
  center: [
    ["display", "block"],
    ["clear", "both"],
    ["margin-top", "0"],
    ["margin-right", "auto"],
    ["margin-bottom", "0"],
    ["margin-left", "auto"],
  ],
};
// The ResizableNodeView wraps the <img> in a `[data-resize-container]` div
// that's already block-level from its parent's perspective — its own
// `display: flex` only governs how it lays out the image + resize handles
// internally, so unlike a bare <img>, it doesn't need `display: block` to
// participate in margin-auto centering, and setting it would clobber the
// library's own flex layout for the handles.
const CONTAINER_ALIGN_DECLARATIONS: Record<ImageAlign, [string, string][]> = {
  left: ALIGN_DECLARATIONS.left,
  right: ALIGN_DECLARATIONS.right,
  center: ALIGN_DECLARATIONS.center.filter(([prop]) => prop !== "display"),
};

const ALL_ALIGN_PROPERTIES = [
  "float",
  "clear",
  "display",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
];

function alignStyleText(align: ImageAlign): string {
  return ALIGN_DECLARATIONS[align].map(([prop, value]) => `${prop}: ${value}`).join("; ");
}

/** Clears any previously-applied alignment properties, then applies the new ones. */
function applyAlignToElement(
  el: HTMLElement,
  align: ImageAlign | null,
  declarations: Record<ImageAlign, [string, string][]> = ALIGN_DECLARATIONS,
) {
  for (const prop of ALL_ALIGN_PROPERTIES) {
    el.style.removeProperty(prop);
  }
  if (align) {
    for (const [prop, value] of declarations[align]) {
      el.style.setProperty(prop, value);
    }
    el.setAttribute("data-align", align);
  } else {
    el.removeAttribute("data-align");
  }
}

/**
 * Extends tiptap's built-in Image node with a left/center/right `align` attribute.
 * Rendered as `data-align` + an inline `style` (float for left/right, auto margins
 * for center) so both the editor and the server-side HTML renderer produce the
 * same layout from the same stored attribute.
 */
export const AlignableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-align"),
        renderHTML: (attributes) => {
          const align = attributes.align as ImageAlign | null;
          if (!align) return {};
          return {
            "data-align": align,
            style: alignStyleText(align),
          };
        },
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImageAlign:
        (align: ImageAlign | null) =>
        ({ commands }) =>
          commands.updateAttributes("image", { align }),
    };
  },

  // When `resize: {enabled: true}` is configured, the base Image extension's
  // addNodeView() returns a ResizableNodeView instance whose own update()
  // only tracks node.type and otherwise no-ops — it never re-applies changed
  // attributes (like `align`) to the DOM after the view is first created. This
  // patches that instance's own update() method (in place — NOT by spreading
  // it into a new object, which would break `this` binding for its other
  // regular class methods like destroy()) to keep the rendered node in sync
  // whenever `align` changes later, without touching resize's own width/height.
  //
  // Alignment applies to `view.dom` — the ResizableNodeView's outer
  // `[data-resize-container]` wrapper — not the inner <img>. That wrapper is
  // `width: fit-content` (see globals.css), so a margin/float on the <img>
  // just inflates the wrapper's own box by that margin instead of moving it:
  // the selection border ends up with a gap around the image, and centering
  // (margin: auto on a box already exactly as wide as the image) has nothing
  // to do. Styling the wrapper itself makes the whole node — image, resize
  // handles, and selection border together — float or center as a unit,
  // matching how the public post renders the bare <img>.
  addNodeView() {
    const parentAddNodeView = this.parent?.();
    if (!parentAddNodeView) return null;
    return (props) => {
      const view = parentAddNodeView(props);
      if (!view) return view;

      const dom = view.dom;
      if (!(dom instanceof HTMLElement)) return view;
      const declarations =
        dom instanceof HTMLImageElement ? ALIGN_DECLARATIONS : CONTAINER_ALIGN_DECLARATIONS;
      const innerImg = dom instanceof HTMLImageElement ? dom : dom.querySelector("img");
      const syncAlign = (align: ImageAlign | null) => {
        applyAlignToElement(dom, align, declarations);
        // The base ResizableNodeView (as of @tiptap/extension-image 3.29) copies
        // every extension-declared HTML attribute — including this extension's
        // own `style`/`data-align` from renderHTML — directly onto the inner
        // <img> too, not just the wrapper it hands us as `dom`. Left alone, both
        // elements float/margin, inflating the wrapper's box by the image's own
        // margin (see the addNodeView comment above for why that matters) — so
        // strip alignment back off the image on every sync, leaving only the
        // wrapper styled.
        if (innerImg && innerImg !== dom) {
          applyAlignToElement(innerImg, null);
        }
      };

      syncAlign((props.node.attrs.align as ImageAlign | null) ?? null);

      const originalUpdate = view.update?.bind(view);
      view.update = (node, decorations, innerDecorations) => {
        const accepted = originalUpdate
          ? originalUpdate(node, decorations, innerDecorations)
          : true;
        if (accepted) {
          syncAlign((node.attrs.align as ImageAlign | null) ?? null);
        }
        return accepted;
      };
      return view;
    };
  },
});
